/**
 * Class rendered by react-loading-skeleton, which is the fallback every
 * lazy-loaded component shows until its chunk arrives. See the loadable()
 * fallback in scripts/generate-component-builder/plugins/component-builder.js.
 */
const SKELETON_SELECTOR = '.react-loading-skeleton';

const NOOP = () => {};

/**
 * Calls back once every lazy-loaded component has replaced its skeleton, i.e.
 * the page has reached its real height.
 *
 * There is no single "hydration finished" event here: each component is its own
 * chunk that resolves whenever its request lands, and React finishes hydrating
 * long before they do. Counting the outstanding skeletons is the closest
 * observable signal.
 *
 * Fires anyway after `timeoutMs`, so a component that never resolves cannot
 * stall the callback forever. Returns a teardown function.
 */
export const onLazyComponentsLoaded = (callback, timeoutMs = 10000) => {
  if (typeof window === 'undefined') return NOOP;

  const outstanding = () => document.querySelectorAll(SKELETON_SELECTOR).length;

  let settled = false;
  let observer = null;
  let timer = null;

  const finish = () => {
    if (settled) return;
    settled = true;

    if (observer) observer.disconnect();
    if (timer !== null) clearTimeout(timer);

    callback();
  };

  // Nothing lazy on this page, so it is already at its real height
  if (outstanding() === 0) {
    finish();
    return NOOP;
  }

  observer = new MutationObserver(() => {
    if (outstanding() === 0) finish();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  timer = setTimeout(finish, timeoutMs);

  return () => {
    settled = true;
    if (observer) observer.disconnect();
    if (timer !== null) clearTimeout(timer);
  };
};

export default onLazyComponentsLoaded;
